// procedureQuiz — deterministic quiz generator for instrument procedure briefings.
//
// Given a ProcedureBrief, generates 3 multiple-choice questions. The questions
// and answer choices are fully deterministic given the same input — no LLM,
// no randomness. We use a seeded slot-selection approach: pick 3 non-overlapping
// question templates that have enough data to fill, then generate plausible
// distractors from the brief's own data.
//
// The "seed" for question selection is: procIdent + transIdent (string hash),
// ensuring the same procedure always shows the same 3 questions.

import type { ProcedureBrief, BriefLeg } from './procedureBrief';

// ── Types ────────────────────────────────────────────────────────────────

export interface QuizQuestion {
  question: string;
  choices: string[];          // 4 choices always; first is correct
  correctIndex: number;       // always 0 — caller should shuffle if desired
  explanation: string;
}

export interface QuizResult {
  questions: QuizQuestion[];
  /** True when there were fewer than 3 viable questions from the data */
  limited: boolean;
}

// ── Distractor helpers ───────────────────────────────────────────────────

/** Nudge an altitude string by ±200, ±500, ±1000 feet for plausible distractors. */
function altDistractors(altStr: string): string[] {
  const n = parseInt(altStr.replace(/[^0-9]/g, ''), 10);
  if (!Number.isFinite(n) || n <= 0) return ['Unknown', '0', 'N/A'];
  const offsets = [200, 500, 1000];
  return offsets.map((d) => String(n % 2 === 0 ? n + d : n - d));
}

/** Nudge a course by ±10, ±20, ±45 degrees (keep 0–360). */
function courseDistractors(course: number): string[] {
  const offsets = [10, 20, 45];
  return offsets.map((d) => {
    const v = (course + d) % 360;
    return `${Math.round(v)}°`;
  });
}

/** Generate fix distractors from other fixes in the brief */
function fixDistractors(fix: string, brief: ProcedureBrief): string[] {
  const pool = [
    ...brief.iafFixes,
    brief.ifFix,
    brief.fafFix,
    brief.mapFix,
    brief.mahpFix,
    ...brief.legs.filter((l) => l.fixIdent && l.fixIdent !== fix).map((l) => l.fixIdent),
  ].filter((f): f is string => !!f && f !== fix);
  // Deduplicate and take up to 3
  const unique = [...new Set(pool)].slice(0, 3);
  while (unique.length < 3) unique.push('N/A');
  return unique;
}

// ── Question templates ───────────────────────────────────────────────────

type QuestionBuilder = (brief: ProcedureBrief) => QuizQuestion | null;

const IAP_TEMPLATES: QuestionBuilder[] = [
  // 1. FAF altitude
  (brief) => {
    if (!brief.fafFix || !brief.fafAltStr) return null;
    const correct = brief.fafAltStr.replace(/[^0-9FL.]/g, '');
    if (!correct) return null;
    const distractors = altDistractors(correct);
    return {
      question: `What is the altitude at the Final Approach Fix (${brief.fafFix})?`,
      choices: [correct, ...distractors],
      correctIndex: 0,
      explanation: `The FAF ${brief.fafFix} has an altitude constraint of ${brief.fafAltStr} feet per the CIFP data.`,
    };
  },

  // 2. Inbound course
  (brief) => {
    if (brief.inboundCourse === null) return null;
    const correct = `${Math.round(brief.inboundCourse)}°`;
    const distractors = courseDistractors(brief.inboundCourse);
    return {
      question: 'What is the inbound course on final approach?',
      choices: [correct, ...distractors],
      correctIndex: 0,
      explanation: `The inbound magnetic course is ${correct} as published in CIFP.`,
    };
  },

  // 3. Lowest CIFP path altitude at MAP / final-segment
  //
  // NOTE (2026-05-18, boss feedback): we used to phrase this as "What is
  // the published DA or MDA for this approach?" That question is
  // ambiguous because an RNAV plate publishes multiple minima lines
  // (S-LPV DA, S-LNAV/VNAV DA, S-LNAV MDA, CIRCLING) with different
  // values per aircraft category, and the per-line minima data isn't
  // yet plumbed into the brief generator (it lives in
  // app.cifp_approach_minima_lines, not in CIFP-blob legs). Until that's
  // wired, scope the question to what we DO have: the lowest path
  // altitude in the CIFP final segment between FAF and MAP — effectively
  // the bottom-of-descent altitude the CIFP itself publishes for the
  // common segment. Quizzing on this is unambiguous because it's a
  // single CIFP-published number, not a category-dependent minima cell.
  (brief) => {
    if (!brief.daStr) return null;
    const correct = brief.daStr.replace(/[^0-9FL]/g, '');
    if (!correct) return null;
    const distractors = altDistractors(correct);
    return {
      question: 'What is the lowest CIFP-published path altitude in the final approach segment (FAF → MAP)?',
      choices: [correct, ...distractors],
      correctIndex: 0,
      explanation:
        `The CIFP publishes ${brief.daStr} feet as the lowest path altitude between the FAF and MAP. ` +
        'NOTE: this is the CIFP path-altitude, not the per-category DA or MDA on the plate — the actual ' +
        'decision altitude / minimum descent altitude depends on which minima line you fly ' +
        '(e.g. S-LPV DA, S-LNAV/VNAV DA, S-LNAV MDA, CIRCLING) and your aircraft\'s approach category.',
    };
  },

  // 4. MAP identification
  (brief) => {
    if (!brief.mapFix) return null;
    const correct = brief.mapFix;
    const distractors = fixDistractors(correct, brief);
    return {
      question: 'What is the Missed Approach Point (MAP) for this approach?',
      choices: [correct, ...distractors],
      correctIndex: 0,
      explanation: `${correct} is the MAP as decoded from the CIFP waypoint-description code.`,
    };
  },

  // 5. IAF
  (brief) => {
    if (brief.iafFixes.length === 0) return null;
    const correct = brief.iafFixes[0];
    const distractors = fixDistractors(correct, brief);
    return {
      question: 'Which fix is the Initial Approach Fix (IAF)?',
      choices: [correct, ...distractors],
      correctIndex: 0,
      explanation: `${correct} is the IAF as decoded from the CIFP waypoint-description code.`,
    };
  },

  // 6. HILPT/PT flag
  (brief) => {
    if (!brief.hasProcedureTurn && !brief.hasHilpt) return null;
    const type = brief.hasHilpt ? 'Hold in lieu of procedure turn (HILPT)' : 'Procedure turn';
    return {
      question: 'Is a procedure turn or hold-in-lieu required for this approach?',
      choices: ['Yes', 'No', 'Only for certain transitions', 'Check NOTAMs'],
      correctIndex: 0,
      explanation: `${type} is published in the CIFP leg data for this approach.`,
    };
  },
];

const SID_TEMPLATES: QuestionBuilder[] = [
  // 1. Top altitude
  (brief) => {
    if (!brief.topAltitudeStr) return null;
    const correct = brief.topAltitudeStr.replace(/[^0-9FL]/g, '');
    if (!correct) return null;
    const distractors = altDistractors(correct);
    return {
      question: 'What is the top altitude published for this departure procedure?',
      choices: [correct, ...distractors],
      correctIndex: 0,
      explanation: `The highest published altitude constraint on this SID is ${brief.topAltitudeStr} feet.`,
    };
  },

  // 2. Initial heading
  (brief) => {
    if (brief.inboundCourse === null) return null;
    const correct = `${Math.round(brief.inboundCourse)}°`;
    const distractors = courseDistractors(brief.inboundCourse);
    return {
      question: 'What is the initial heading or course for this departure?',
      choices: [correct, ...distractors],
      correctIndex: 0,
      explanation: `The initial published magnetic course/heading is ${correct}.`,
    };
  },

  // 3. First named fix
  (brief) => {
    const fixedLegs = brief.legs.filter((l) => l.fixIdent && l.legType !== 'IF');
    if (fixedLegs.length === 0) return null;
    const correct = fixedLegs[0].fixIdent;
    const distractors = fixDistractors(correct, brief);
    return {
      question: 'What is the first named fix on this departure procedure?',
      choices: [correct, ...distractors],
      correctIndex: 0,
      explanation: `${correct} is the first fix after departure as published in CIFP.`,
    };
  },

  // 4. Any speed restriction
  (brief) => {
    const speedLeg = brief.legs.find((l) => l.speedKt !== null);
    if (!speedLeg) return null;
    const correct = `${speedLeg.speedKt} kt`;
    const spd = speedLeg.speedKt!;
    const distractors = [String(spd + 10) + ' kt', String(spd - 10) + ' kt', 'No restriction'];
    return {
      question: speedLeg.fixIdent
        ? `What is the speed restriction at ${speedLeg.fixIdent} on this departure?`
        : 'What is the published speed restriction on this departure?',
      choices: [correct, ...distractors],
      correctIndex: 0,
      explanation: `The CIFP record shows a ${correct} speed limit for this leg.`,
    };
  },
];

const STAR_TEMPLATES: QuestionBuilder[] = [
  // 1. Top altitude
  (brief) => {
    if (!brief.topAltitudeStr) return null;
    const correct = brief.topAltitudeStr.replace(/[^0-9FL]/g, '');
    if (!correct) return null;
    const distractors = altDistractors(correct);
    return {
      question: 'What is the top altitude for this arrival procedure?',
      choices: [correct, ...distractors],
      correctIndex: 0,
      explanation: `The highest published altitude constraint on this STAR is ${brief.topAltitudeStr} feet.`,
    };
  },

  // 2. Entry fix
  (brief) => {
    if (brief.transitionEntryFixes.length === 0) return null;
    const correct = brief.transitionEntryFixes[0];
    const distractors = fixDistractors(correct, brief);
    return {
      question: 'What is the transition entry fix for this arrival?',
      choices: [correct, ...distractors],
      correctIndex: 0,
      explanation: `${correct} is the entry fix for the selected transition.`,
    };
  },

  // 3. First altitude window
  (brief) => {
    const legWithAlt = brief.legs.find((l) => l.altConstraint && l.fixIdent);
    if (!legWithAlt) return null;
    const correct = legWithAlt.altConstraint!;
    const n = parseInt(correct.replace(/[^0-9]/g, ''), 10);
    const distractors = Number.isFinite(n) && n > 0
      ? altDistractors(String(n)).map((d) => `${correct.replace(/[0-9]+/, d)}`)
      : ['Unknown', 'No restriction', 'Check chart'];
    return {
      question: `What is the altitude restriction at ${legWithAlt.fixIdent}?`,
      choices: [correct, ...distractors.slice(0, 3)],
      correctIndex: 0,
      explanation: `CIFP publishes an altitude of ${correct} feet at ${legWithAlt.fixIdent}.`,
    };
  },

  // 4. Last fix before runway transition
  (brief) => {
    const lastFixed = [...brief.legs].reverse().find((l) => l.fixIdent);
    if (!lastFixed) return null;
    const correct = lastFixed.fixIdent;
    const distractors = fixDistractors(correct, brief);
    return {
      question: 'What is the last named fix on this arrival route?',
      choices: [correct, ...distractors],
      correctIndex: 0,
      explanation: `${correct} is the last named fix in the CIFP transition legs.`,
    };
  },
];

// ── Main generator ───────────────────────────────────────────────────────

/** Simple deterministic string hash (djb2 variant). */
function hashStr(s: string): number {
  let h = 5381;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) + h) ^ s.charCodeAt(i);
    h = h >>> 0; // keep unsigned 32-bit
  }
  return h;
}

export function generateQuiz(brief: ProcedureBrief): QuizResult {
  const templates: QuestionBuilder[] =
    brief.routeType === 'IAP'  ? IAP_TEMPLATES :
    brief.routeType === 'SID'  ? SID_TEMPLATES :
                                  STAR_TEMPLATES;

  // Use the seed to pick an ordering of template indices.
  const seed = hashStr(`${brief.procIdent}::${brief.transitionIdent}`);

  // Build viable questions (templates that return non-null)
  const viable: QuizQuestion[] = [];
  for (const tmpl of templates) {
    const q = tmpl(brief);
    if (q) viable.push(q);
  }

  // Deterministically pick 3 (or fewer if not enough viable)
  const target = 3;
  const picked: QuizQuestion[] = [];
  const used = new Set<number>();

  // First pass: pick by seed order
  for (let attempt = 0; picked.length < target && attempt < viable.length * 2; attempt++) {
    const idx = (seed + attempt * 7) % viable.length;
    if (!used.has(idx)) {
      used.add(idx);
      picked.push(viable[idx]);
    }
  }

  // Fill any remaining slots in order
  for (let i = 0; picked.length < target && i < viable.length; i++) {
    if (!used.has(i)) {
      used.add(i);
      picked.push(viable[i]);
    }
  }

  return {
    questions: picked,
    limited: picked.length < target,
  };
}
